import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Params, RouterModule } from '@angular/router';
import { catchError, concatMap, finalize } from 'rxjs/operators';
import { of } from 'rxjs';

import { Betaaltransactie, BetaaltransactieStatus } from '@ap/interface-apic-betaalverzoek';
import { NBAOutcome, NextBestAction } from '@ap/interface-pega';
import { ApThemeService } from '@ap/theme';
import { ApButtonComponent } from '@ap/button';
import { ApContentModule } from '@ap/content';
import { ApPersistenceModule } from '@ap/persistence';
import { SessiebeheerModule } from '@ap/interface-apic-sessiebeheer';

import { BetaalverzoekErrorType } from '../../betaalverzoek.model';
import { BetaalverzoekService } from '../../betaalverzoek.service';
import { BetaalverzoekNavigation } from '../../betaalverzoek.navigation';
import { TransactionResultComponent } from '../../domain/transaction-result-component/transaction-result.component';

import { ResultPageService } from './result-page.service';

/**
 * @ngdoc component
 * @name ResultPageComponent
 *
 * @description
 * Component that provides functionality for the result landing page
 */
@Component({
    selector: 'result-page',
    templateUrl: './result-page.component.html',
    styleUrls: ['./result-page.component.scss'],
    imports: [ApButtonComponent, ApContentModule, ApPersistenceModule, RouterModule, SessiebeheerModule, TransactionResultComponent]
})
export class ResultPageComponent implements OnInit {
    public betaaltransactie: Betaaltransactie;
    public pegaContainerName = 'BetaalverzoekBetaaldPagina';
    public nbaLoading = false;
    public nextBestAction: NextBestAction;

    public constructor(
        private readonly activatedRoute: ActivatedRoute,
        private readonly betaalverzoekNavigation: BetaalverzoekNavigation,
        private readonly betaalverzoekService: BetaalverzoekService,
        private readonly resultPageService: ResultPageService,
        private readonly themeService: ApThemeService
    ) {}

    public ngOnInit(): void {
        this.betaalverzoekService
            .terminateSession()
            .pipe(
                concatMap(() => this.activatedRoute.queryParams),
                concatMap((params: Params) => {
                    this.betaalverzoekService.setTransactieId(params.orderId);
                    return this.betaalverzoekService.getBetaaltransactie(params.orderId, null);
                })
            )
            .subscribe({
                next: (betaaltransactie: Betaaltransactie) => {
                    this.betaaltransactie = betaaltransactie;
                    this.betaalverzoekService.navigateToErrorPageOnInvalidStatus(this.betaaltransactie.status);
                    if (this.betaaltransactie.status === BetaaltransactieStatus.SUCCESS) {
                        this.resultPageService.setPayedCookie(betaaltransactie.betaalverzoek_id);
                        this.getNextBestActionProducts();
                    }
                },
                error: () => {
                    this.betaalverzoekNavigation.toErrorPage(
                        BetaalverzoekService.generateErrorData(BetaalverzoekErrorType.TRANSACTION_REQUEST_SERVICE_UNAVAILABLE, false)
                    );
                }
            });
        this.betaalverzoekService.setShowHeaderIcon(false);
    }

    public nbaClicked(): void {
        this.resultPageService.respondToNextBestAction({ nba: this.nextBestAction, outcome: NBAOutcome.CLICKED }, this.pegaContainerName);
        this.betaalverzoekNavigation.external(this.nextBestAction.link);
    }

    private getNextBestActionProducts(): void {
        if (this.themeService.getTheme() === 'sns') {
            return;
        }

        this.nbaLoading = true;

        this.resultPageService
            .getNextBestAction(this.pegaContainerName)
            .pipe(
                catchError(() => {
                    return of();
                }),
                finalize(() => (this.nbaLoading = false))
            )
            .subscribe((nbaProduct) => {
                this.nextBestAction = nbaProduct;

                this.resultPageService.respondToNextBestAction({ nba: nbaProduct, outcome: NBAOutcome.IMPRESSION }, this.pegaContainerName);
            });
    }
}
